import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useActivateReferenceDBAsset,
  useReferenceDBAsset,
  useReferenceDBAssets,
  useRegisterReferenceDBAsset,
} from "@/hooks/useReferenceDBAssets";
import type {
  ReferenceDBAsset,
  ReferenceDBAssetCreate,
} from "@/types/referenceDBAsset";

// ───────────────────────────── 보조 유틸 ─────────────────────────────

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncatePath(path: string, maxLen = 50): string {
  if (path.length <= maxLen) return path;
  const half = Math.floor(maxLen / 2);
  return `${path.slice(0, half)}...${path.slice(-half)}`;
}

// ───────────────────────────── 상세 패널 ─────────────────────────────

function AssetDetailPanel({
  assetId,
  onClose,
}: {
  assetId: number;
  onClose: () => void;
}) {
  const { data: asset, isLoading, error } = useReferenceDBAsset(assetId);
  const activateMutation = useActivateReferenceDBAsset();

  function handleActivate() {
    if (!asset || asset.is_active) return;
    activateMutation.mutate(asset.id, {
      onSuccess: () => {
        // 상세 패널은 열어두고 목록은 자동 갱신됨
      },
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      {/* 오버레이 — 클릭 시 패널 닫기 */}
      <button
        type="button"
        className="absolute inset-0 bg-black/30 cursor-default"
        onClick={onClose}
        aria-label="패널 닫기"
      />
      {/* 패널 */}
      <div className="relative bg-white w-full max-w-md h-full shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">asset 상세</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="닫기"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-5">
          {isLoading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-8 bg-gray-200 rounded" />
              ))}
            </div>
          ) : error ? (
            <p className="text-red-600 text-sm">
              asset 정보를 불러올 수 없습니다.
            </p>
          ) : asset ? (
            <div className="space-y-5">
              {/* 상태 배지 */}
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                    asset.is_active
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {asset.is_active ? "활성" : "비활성"}
                </span>
                <span className="text-lg font-bold text-gray-900">
                  {asset.name}
                </span>
                <span className="text-sm text-gray-500">v{asset.version}</span>
              </div>

              {/* 메타 정보 */}
              <dl className="space-y-2 text-sm">
                <div className="flex gap-2">
                  <dt className="w-28 text-gray-500 shrink-0">ID</dt>
                  <dd className="text-gray-900 font-mono">{asset.id}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 text-gray-500 shrink-0">파일 경로</dt>
                  <dd
                    className="text-gray-900 font-mono text-xs break-all"
                    title={asset.file_path}
                  >
                    {truncatePath(asset.file_path)}
                  </dd>
                </div>
                {asset.index_path && (
                  <div className="flex gap-2">
                    <dt className="w-28 text-gray-500 shrink-0">인덱스 경로</dt>
                    <dd
                      className="text-gray-900 font-mono text-xs break-all"
                      title={asset.index_path}
                    >
                      {truncatePath(asset.index_path)}
                    </dd>
                  </div>
                )}
                <div className="flex gap-2">
                  <dt className="w-28 text-gray-500 shrink-0">SHA-256</dt>
                  <dd className="text-gray-900 font-mono text-xs break-all">
                    {asset.sha256}
                  </dd>
                </div>
                {asset.record_count !== null && (
                  <div className="flex gap-2">
                    <dt className="w-28 text-gray-500 shrink-0">레코드 수</dt>
                    <dd className="text-gray-900">
                      {asset.record_count.toLocaleString("ko-KR")}개
                    </dd>
                  </div>
                )}
                {asset.data_source_id !== null && (
                  <div className="flex gap-2">
                    <dt className="w-28 text-gray-500 shrink-0">출처 ID</dt>
                    <dd className="text-gray-900 font-mono">
                      {asset.data_source_id}
                    </dd>
                  </div>
                )}
                {asset.notes && (
                  <div className="flex gap-2">
                    <dt className="w-28 text-gray-500 shrink-0">비고</dt>
                    <dd className="text-gray-900">{asset.notes}</dd>
                  </div>
                )}
                <div className="flex gap-2">
                  <dt className="w-28 text-gray-500 shrink-0">등록일</dt>
                  <dd className="text-gray-900">
                    {formatDateTime(asset.created_at)}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-28 text-gray-500 shrink-0">갱신일</dt>
                  <dd className="text-gray-900">
                    {formatDateTime(asset.updated_at)}
                  </dd>
                </div>
              </dl>

              {/* 활성화 버튼 */}
              {!asset.is_active && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleActivate}
                    disabled={activateMutation.isPending}
                    className="w-full py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {activateMutation.isPending
                      ? "전환 중..."
                      : "이 버전으로 활성화"}
                  </button>
                  {activateMutation.isError && (
                    <p className="text-xs text-red-600 mt-1 text-center">
                      활성화 중 오류가 발생했습니다. 다시 시도해 주세요.
                    </p>
                  )}
                  {activateMutation.isSuccess && (
                    <p className="text-xs text-green-600 mt-1 text-center">
                      활성 포인터가 전환되었습니다.
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────── 등록 모달 ─────────────────────────────

const EMPTY_FORM: ReferenceDBAssetCreate = {
  name: "",
  version: "",
  source_path: "",
  data_source_id: null,
  expected_sha256: null,
  notes: null,
  activate: false,
};

function RegisterModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<ReferenceDBAssetCreate>({ ...EMPTY_FORM });
  const [sha256Input, setSha256Input] = useState("");
  const [notesInput, setNotesInput] = useState("");
  const registerMutation = useRegisterReferenceDBAsset();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: ReferenceDBAssetCreate = {
      ...form,
      expected_sha256: sha256Input.trim() || null,
      notes: notesInput.trim() || null,
    };
    registerMutation.mutate(payload, {
      onSuccess: () => {
        onClose();
      },
    });
  }

  function field(
    id: keyof ReferenceDBAssetCreate,
    value: string,
    setter: (v: string) => void,
    label: string,
    required: boolean,
    hint?: string,
  ) {
    return (
      <div>
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => setter(e.target.value)}
          required={required}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 오버레이 */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40 cursor-default"
        onClick={onClose}
        aria-label="모달 닫기"
      />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg overflow-y-auto max-h-screen">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">asset 등록</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="닫기"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
            대용량 파일(수 GB)은 서버에 먼저 배치 후 경로를 입력하거나 CLI
            스크립트를 사용하세요. 이 화면은 메타 등록 및 중소 규모용입니다.
          </div>

          {field(
            "name",
            form.name,
            (v) => setForm((f) => ({ ...f, name: v })),
            "논리 asset 이름",
            true,
            "예: standard-nist (버전 공유 키)",
          )}
          {field(
            "version",
            form.version,
            (v) => setForm((f) => ({ ...f, version: v })),
            "버전",
            true,
            "예: 2026.1",
          )}
          {field(
            "source_path",
            form.source_path,
            (v) => setForm((f) => ({ ...f, source_path: v })),
            "서버 파일시스템 경로",
            true,
            "서버에 이미 배치된 원본 경로",
          )}
          {field(
            "expected_sha256",
            sha256Input,
            setSha256Input,
            "SHA-256 해시 (선택)",
            false,
            "무결성 검증용. 비워두면 검증 생략",
          )}

          {/* 출처 ID */}
          <div>
            <label
              htmlFor="data_source_id"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              출처 ID (선택)
            </label>
            <input
              id="data_source_id"
              type="number"
              min={1}
              value={form.data_source_id ?? ""}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  data_source_id: e.target.value
                    ? Number(e.target.value)
                    : null,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* 비고 */}
          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              비고 (선택)
            </label>
            <textarea
              id="notes"
              rows={2}
              value={notesInput}
              onChange={(e) => setNotesInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* 즉시 활성화 */}
          <div className="flex items-center gap-2">
            <input
              id="activate"
              type="checkbox"
              checked={form.activate ?? false}
              onChange={(e) =>
                setForm((f) => ({ ...f, activate: e.target.checked }))
              }
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="activate" className="text-sm text-gray-700">
              등록 즉시 활성 포인터로 전환
            </label>
          </div>

          {registerMutation.isError && (
            <p className="text-xs text-red-600">
              등록 중 오류가 발생했습니다. 경로·중복 여부를 확인하세요.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={registerMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {registerMutation.isPending ? "등록 중..." : "등록"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ───────────────────────────── 메인 페이지 ─────────────────────────────

export default function AdminReferenceDBPage() {
  const { data, isLoading, error } = useReferenceDBAssets();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showRegister, setShowRegister] = useState(false);

  const assets = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Link
        to="/admin"
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        ← 관리 홈
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">
            참조 DB asset 관리
          </h1>
          <p className="text-gray-600">
            대용량 표준 라이브러리 asset 등록·버전 관리·활성 포인터 전환. 관리자
            전용.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowRegister(true)}
          className="flex-shrink-0 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
        >
          + asset 등록
        </button>
      </div>

      {/* 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            등록 asset 목록
          </h2>
          {!isLoading && !error && (
            <p className="text-sm text-gray-500 mt-0.5">총 {total}개</p>
          )}
        </div>

        {isLoading ? (
          <div className="p-6 animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-gray-200 rounded" />
            ))}
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-red-600 font-medium">
              asset 목록을 불러오는 중 오류가 발생했습니다.
            </p>
          </div>
        ) : assets.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 font-medium mb-2">
              등록된 asset이 없습니다
            </p>
            <p className="text-sm text-gray-400">
              "+ asset 등록" 버튼으로 새 asset을 추가하거나 CLI 스크립트를
              사용하세요.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이름
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    버전
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    레코드 수
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    등록일
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상세
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assets.map((asset: ReferenceDBAsset) => (
                  <tr
                    key={asset.id}
                    className={`hover:bg-gray-50 ${asset.is_active ? "bg-green-50" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          asset.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {asset.is_active ? "활성" : "비활성"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {asset.name}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm hidden sm:table-cell">
                      v{asset.version}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600 hidden md:table-cell">
                      {asset.record_count !== null
                        ? asset.record_count.toLocaleString("ko-KR")
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">
                      {formatDateTime(asset.created_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => setSelectedId(asset.id)}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        상세
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 상세 패널 */}
      {selectedId !== null && (
        <AssetDetailPanel
          assetId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}

      {/* 등록 모달 */}
      {showRegister && <RegisterModal onClose={() => setShowRegister(false)} />}
    </div>
  );
}
